import { useQuery } from "@tanstack/react-query";
import invariant from "tiny-invariant";

import moment from "moment-timezone";
import { callLcosService } from "../../data/lcosServices";
import { useAccessToken } from "../use-access-token";
import { useFamilyConstants } from "../use-families";
import { FAMILY_ROLE_INTERNAL_CODES, findFamilyRoleIdByInternalCode } from "../use-families/family-role-utils";
import type { FamilyRelationshipDetails, FamilyRole } from "../use-families";
import { useTimezoneConverter } from "../use-timezone-converter";
import { inquiryQueryKeys } from "./query-keys";
import type { InquiryDashboardResponsePayload } from "./types";

interface UseInquiryDashboardProps {
  centerId: string;
  inquiryId?: string;
}

const memberHasPrimaryContactRole = (roles: FamilyRole[] | undefined, primaryContactRoleId: string): boolean => {
  if (!primaryContactRoleId.length) {
    return false;
  }
  return (roles ?? []).some(role => role.roleId === primaryContactRoleId);
};

const memberHasEmergencyRelationshipToStudent = (
  memberId: number,
  studentId: number,
  relationships: FamilyRelationshipDetails[] | undefined,
  emergencyContactValue: string
): boolean => {
  if (!emergencyContactValue.length) {
    return false;
  }
  const emergency = String(emergencyContactValue);
  for (const rel of relationships ?? []) {
    if (String(rel.relationship?.value) !== emergency) {
      continue;
    }
    if (
      rel.subjectContactId === studentId &&
      rel.objectContactId === memberId &&
      rel.subjectContactId !== rel.objectContactId
    ) {
      return true;
    }
    if (
      rel.objectContactId === studentId &&
      rel.subjectContactId === memberId &&
      rel.subjectContactId !== rel.objectContactId
    ) {
      return true;
    }
  }
  return false;
};

const findRelationDisplayNameForContactsTable = (
  memberId: number,
  studentId: number,
  relationships: FamilyRelationshipDetails[] | undefined,
  emergencyContactValue: string
): string | undefined => {
  const emergency = String(emergencyContactValue);
  for (const rel of relationships ?? []) {
    const involvesStudentAndMember =
      (rel.subjectContactId === studentId && rel.objectContactId === memberId) ||
      (rel.objectContactId === studentId && rel.subjectContactId === memberId);
    if (!involvesStudentAndMember || rel.subjectContactId === rel.objectContactId) {
      continue;
    }
    if (String(rel.relationship?.value) === emergency) {
      continue;
    }
    return rel.relationship?.name;
  }
  return undefined;
};

export const formatDashboardData = (
  {
    student,
    service,
    source,
    familyInfo,
    upcomingEvents,
    pastEvents
  }: Pick<
    InquiryDashboardResponsePayload,
    "student" | "service" | "source" | "familyInfo" | "upcomingEvents" | "pastEvents"
  >,
  centerTimezone: string,
  EMERGENCY_CONTACT_VALUE: string,
  PRIMARY_CONTACT_VALUE: string
) => {
  const safeFamilyInfo = familyInfo ?? [];
  const safeUpcomingEvents = upcomingEvents ?? [];
  const safePastEvents = pastEvents ?? [];
  const emergencyValue = String(EMERGENCY_CONTACT_VALUE ?? "");

  const convertEvent = (event: (typeof upcomingEvents)[number]) => ({
    ...event,
    scheduledDateTime: moment.utc(event.scheduledDateTime).tz(centerTimezone).format(),
    ...(event.endDateTime && { endDateTime: moment.utc(event.endDateTime).tz(centerTimezone).format() })
  });

  const attendees = safeFamilyInfo.map(({ id, displayName }) => ({ value: id, label: displayName }));
  attendees.push({ value: student.id, label: student.displayName });
  attendees.sort((left, right) => left.label.localeCompare(right.label));

  const studentId = student.id;

  return {
    inquiry: [
      {
        studentId: student.id,
        studentName: student.displayName,
        grade: student.grade,
        service: service,
        source: source,
        gender: student.gender,
        school_name: student.schoolName
      }
    ],
    contacts: safeFamilyInfo
      .filter(member => {
        const isPrimary = memberHasPrimaryContactRole(member.roles, PRIMARY_CONTACT_VALUE);
        const isEmergency = memberHasEmergencyRelationshipToStudent(
          member.id,
          studentId,
          member.relationships,
          emergencyValue
        );
        return isPrimary || isEmergency;
      })
      .map(familyMemberInfo => {
        const isPrimary = memberHasPrimaryContactRole(familyMemberInfo.roles, PRIMARY_CONTACT_VALUE);
        const isEmergencyContact = memberHasEmergencyRelationshipToStudent(
          familyMemberInfo.id,
          studentId,
          familyMemberInfo.relationships,
          emergencyValue
        );

        return {
          id: familyMemberInfo.id,
          displayName: familyMemberInfo.displayName,
          email: familyMemberInfo.email,
          preferredPhone: familyMemberInfo.preferredPhone?.phoneNumber ?? "",
          source: source,
          contactType:
            isPrimary && isEmergencyContact
              ? "Primary/Emergency"
              : isPrimary
                ? "Primary"
                : isEmergencyContact
                  ? "Emergency"
                  : "",
          relation: findRelationDisplayNameForContactsTable(
            familyMemberInfo.id,
            studentId,
            familyMemberInfo.relationships,
            emergencyValue
          )
        };
      }),
    upcomingEvents: safeUpcomingEvents.map(convertEvent),
    pastEvents: safePastEvents.map(convertEvent),
    familyInfo: safeFamilyInfo,
    student,
    attendees
  };
};

export type InquiryDashboard = ReturnType<typeof formatDashboardData>;
export type DashboardInquiry = InquiryDashboard["inquiry"][number];
export type DashboardContact = InquiryDashboard["contacts"][number];
export type DashboardUpcomingEvent = InquiryDashboard["upcomingEvents"][number];

export const useInquiryDashboard = ({ centerId, inquiryId }: UseInquiryDashboardProps) => {
  const { getToken } = useAccessToken();
  const { EMERGENCY_CONTACT_VALUE, roles } = useFamilyConstants();
  const PRIMARY_CONTACT_VALUE =
    findFamilyRoleIdByInternalCode(roles, FAMILY_ROLE_INTERNAL_CODES.PRIMARY_CONTACT, { activeOnly: true }) ??
    findFamilyRoleIdByInternalCode(roles, FAMILY_ROLE_INTERNAL_CODES.PRIMARY_CONTACT) ??
    "";
  const { timezone } = useTimezoneConverter(centerId);

  return useQuery({
    queryKey: inquiryQueryKeys.dashboard(centerId, inquiryId),
    enabled: Boolean(centerId.length && inquiryId?.length),
    queryFn: async () => {
      invariant(inquiryId, "centerId is required");
      return getInquiryDashboard(await getToken(), centerId, inquiryId);
    },
    select: data => formatDashboardData(data, timezone, String(EMERGENCY_CONTACT_VALUE ?? ""), PRIMARY_CONTACT_VALUE)
  });
};

export const getInquiryDashboard = async (accessToken: string, centerId: string, inquiryId: string) => {
  return callLcosService<InquiryDashboardResponsePayload>(
    accessToken,
    `/api/inquiries/${centerId}/${inquiryId}/dashboard`,
    "GET"
  );
};
